import {
  getHousehold,
  getStreamingDeviceList,
  getCompoundHouseholdStreamingDeviceList,
  HouseholdDevice,
  KuxFilteredCategoriesDTO,
} from '@kaltura-ott/tvpil';
import { observable, action } from 'mobx';
import { lastValueFrom } from 'rxjs';
import { singleton } from 'tsyringe';

import { errorHandling } from '../../utils/error-handling';
import { ErrorPriority } from '../error';

export interface Household {
  devicesLimit: number;
  concurrentLimit: number;
  totalStreamingDevices?: number;
  frequencyNextDeviceAction: number;
  isFrequencyEnabled: boolean;
}
/**
 * @category Store
 */
@singleton()
export class ConcurrencyStore {
  static moduleName = 'ConcurrencyStore';

  @observable isReady = false;
  @observable household?: Household;
  @observable streamingDevices?: HouseholdDevice[];
  @observable concurrencyPlayerErrorTtl?: number;
  @observable showManageDevicesButton?: boolean;
  @observable concurrencyBuymore?: boolean;
  subpageProperty?: KuxFilteredCategoriesDTO['subpageProperty'];

  init() {
    if (this.subpageProperty) {
      this.concurrencyPlayerErrorTtl = this.subpageProperty.concurrencyPlayerErrorTtl * 1000; // milliseconds
      this.showManageDevicesButton = this.subpageProperty.showManageDevicesButton;
      this.concurrencyBuymore = this.subpageProperty.concurrencyBuymore;
    }
  }

  @action
  @errorHandling({ type: ErrorPriority.low })
  async getConcurrencyDetails() {
    const household = await lastValueFrom(getHousehold());

    const streamingDevices = await lastValueFrom(getCompoundHouseholdStreamingDeviceList());
    this.streamingDevices = streamingDevices?.items;

    this.household = {
      devicesLimit: household?.devicesLimit,
      concurrentLimit: household?.concurrentLimit,
      totalStreamingDevices: streamingDevices?.totalCount,
      frequencyNextDeviceAction: household?.frequencyNextDeviceAction * 1000,
      isFrequencyEnabled: household?.isFrequencyEnabled,
    };
  }

  /**
   * Get concurrency limitations
   */
  @action
  @errorHandling({ type: ErrorPriority.low })
  async getConcurrencyLimitations(skipTotalStreamingDevices = false) {
    const household = await lastValueFrom(getHousehold());
    const totalStreamingDevices = skipTotalStreamingDevices
      ? this.household?.totalStreamingDevices
      : (await lastValueFrom(getStreamingDeviceList()))?.totalCount;

    this.household = {
      devicesLimit: household?.devicesLimit,
      concurrentLimit: household?.concurrentLimit,
      totalStreamingDevices,
      frequencyNextDeviceAction: household?.frequencyNextDeviceAction * 1000,
      isFrequencyEnabled: household?.isFrequencyEnabled,
    };
  }
}
